import { Pencil, Trash2 } from 'lucide-react'
import { cloneElement, isValidElement, useState } from 'react'
import type { CSSProperties, ReactElement, ReactNode } from 'react'

import { useScheduleI18n } from '../../i18n'
import type { SwitchboardRoutingEnum } from '../../types'
import { dsText, fontSize, tokenVar } from '../../utils'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'

const DefaultEditIcon = ({ color }: { color: string }) => (
  <Pencil size={18} color={color} />
)

const DefaultTrashIcon = ({ color }: { color: string }) => (
  <Trash2 size={18} color={color} />
)

export interface ScheduleInfoProps {
  /** Schedule name */
  name: string
  /** Schedule description */
  description?: string
  /** Current routing status */
  routingStatus?: SwitchboardRoutingEnum
  /** Timezone display name */
  timeZoneName?: string
  /** Whether user can edit */
  canEdit?: boolean
  /** Whether user can delete */
  canDelete?: boolean
  /** Optional back button element rendered inline with the schedule name */
  backButton?: ReactNode
  /** Called when edit info button is clicked */
  onEditInfo?: () => void
  /** Called when delete button is clicked */
  onDelete?: () => void
  /** Custom edit icon */
  editIcon?: ReactNode
  /** Custom delete icon */
  deleteIcon?: ReactNode
  /** Custom class name */
  className?: string
}

const renderTokenizedIcon = (
  icon: ReactNode | undefined,
  fallback: ReactNode,
  color: string,
): ReactNode => {
  if (isValidElement(icon)) {
    const iconElement = icon as ReactElement<{
      color?: string
      stroke?: string
      fill?: string
      style?: CSSProperties
    }>
    return cloneElement(iconElement, {
      color,
      stroke: color,
      fill: color,
      style: {
        color,
        stroke: color,
        fill: color,
        ...(iconElement.props.style ?? {}),
      },
    })
  }

  return icon ?? fallback
}

/**
 * Schedule information display
 * Shows name, description, and current routing status
 */
export const ScheduleInfo = ({
  name,
  description,
  canEdit = false,
  canDelete = false,
  backButton,
  onEditInfo,
  onDelete,
  editIcon,
  deleteIcon,
  className = '',
}: ScheduleInfoProps) => {
  const { text } = useScheduleI18n()
  const editIconColor = tokenVar('blue.fg')
  const deleteIconColor = tokenVar('red.fg')
  const editScheduleTitle = text(
    'schedule.detail.actions.editScheduleTitle',
    'Edit Schedule',
  )
  const deleteScheduleTitle = text(
    'schedule.detail.actions.deleteScheduleTitle',
    'Delete Schedule',
  )
  const deleteScheduleModalTitle = text(
    'schedule.detail.actions.deleteScheduleModalTitle',
    'Delete schedule',
  )
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const handleDeleteClick = () => {
    if (canDelete && onDelete) {
      setIsDeleteModalOpen(true)
    }
  }

  const handleConfirmDelete = () => {
    if (canDelete && onDelete) {
      onDelete()
    }
    setIsDeleteModalOpen(false)
  }

  return (
    <>
      <div
        className={`schedule-info ${className}`}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          boxSizing: 'border-box',
          backgroundColor: tokenVar('bg.default'),
          padding: '12px 16px',
          marginBottom: '20px',
          paddingBottom: '12px',
          borderBottom: `1px solid ${tokenVar('border.default')}`,
          gap: '20px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {backButton}
            <h2
              style={{
                padding: 0,
                margin: 0,
                fontFamily: dsText.H4.fontFamily,
                fontSize: fontSize(dsText.H4.fontSize),
                fontWeight: dsText.H4.fontWeight,
                lineHeight: dsText.H4.lineHeight,
                letterSpacing: dsText.H4.letterSpacing,
                color: tokenVar('fg.default'),
              }}
            >
              {name}
            </h2>
          </div>
          {description && (
            <p
              style={{
                padding: 0,
                margin: 0,
                fontFamily: dsText.Body2.fontFamily,
                fontSize: fontSize(dsText.Body2.fontSize),
                fontWeight: dsText.Body2.fontWeight,
                lineHeight: dsText.Body2.lineHeight,
                color: tokenVar('fg.subtle'),
              }}
            >
              {description}
            </p>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            minWidth: '120px',
          }}
        >
          {/* Action buttons - right aligned */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {canEdit && onEditInfo && (
              <button
                type="button"
                onClick={onEditInfo}
                title={editScheduleTitle}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  cursor: 'pointer',
                  backgroundColor: tokenVar('blue.subtle'),
                  border: 'none',
                  borderRadius: '6px',
                  color: tokenVar('blue.fg'),
                  transition: 'background-color 0.2s',
                }}
              >
                {renderTokenizedIcon(
                  editIcon,
                  <DefaultEditIcon color={tokenVar('blue.fg')} />,
                  editIconColor,
                )}
              </button>
            )}
            {canDelete && onDelete && (
              <button
                type="button"
                onClick={handleDeleteClick}
                title={deleteScheduleTitle}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  cursor: 'pointer',
                  backgroundColor: tokenVar('red.subtle'),
                  border: 'none',
                  borderRadius: '6px',
                  color: tokenVar('red.fg'),
                  transition: 'background-color 0.2s',
                }}
              >
                {renderTokenizedIcon(
                  deleteIcon,
                  <DefaultTrashIcon color={tokenVar('red.fg')} />,
                  deleteIconColor,
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={deleteScheduleModalTitle}
        itemName={name}
      />
    </>
  )
}
